import type { ChangeEvent, MouseEvent as MouseEventReact, RefObject } from 'react';
import type { GeneralType } from '@/redux/general';
import { ConfigSlice } from '@/redux/config';
import { store } from '@/redux/store';

export const clickWithStop = (e: MouseEventReact<HTMLButtonElement>, cb: () => void): void => {
  e.preventDefault();
  if (e.target === e.currentTarget) {
    e.stopPropagation();
    cb();
  }
};

/**
 * Applies a character filter to an input while it is being typed or pasted and
 * returns the value that survived.
 *
 * The DOM node is written directly instead of relying on the value prop: when
 * every character the user just entered gets filtered out, the prop does not
 * change, React re-renders nothing, and the rejected character would stay
 * visible in the field. Writing the node also keeps React from touching it
 * afterwards, which is what would otherwise throw the caret to the end.
 *
 * The caret lands where the user left it, counted in surviving characters, so
 * typing in the middle of a number and pasting both stay put.
 */
export const filterInput = (event: ChangeEvent<HTMLInputElement>, filter?: (value: string) => string): string => {
  const element = event.target;
  if (!filter) return element.value;
  const raw = element.value;
  const filtered = filter(raw);
  if (filtered === raw) return filtered;
  const caret = filter(raw.slice(0, element.selectionStart ?? raw.length)).length;
  element.value = filtered;
  element.setSelectionRange(caret, caret);
  return filtered;
};

export const path = (p: string): string => {
  const base = import.meta.env.BASE_URL === '/' ? '' : import.meta.env.BASE_URL;
  return base.concat(p);
};

export const openUrl = (url: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const clickHasNode = (event: MouseEvent | TouchEvent | FocusEvent, element: RefObject<HTMLElement>): boolean => {
  return Boolean(element.current && element.current.contains(event.target as Node));
};

interface AppendSession {
  session: GeneralType['session'];
  search: GeneralType['windowLocationSearch'];
  setSearch: (search: GeneralType['windowLocationSearch']) => void;
}
export const appendSession = (props: AppendSession): void => {
  if (!props.session) return;
  if (!props.search) {
    const newSearchValue = props.search + '?' + props.session.name + '=' + props.session.id;
    props.setSearch(newSearchValue);
  } else {
    const newSearchValue = props.search + '&' + props.session.name + '=' + props.session.id;
    props.setSearch(newSearchValue);
  }
};

interface SetRoute extends AppendSession {
  done: boolean;
}
export const setRoute = (props: SetRoute): void => {
  if (location.hash.length > 0 || location.href.indexOf('#') > -1) {
    return;
  }

  let urlPath = 'danke.php';

  appendSession({ session: props.session, search: props.search, setSearch: props.setSearch });

  if (!props.done) {
    urlPath = '';
  }

  if (urlPath === 'danke.php') {
    console.log('CASE 2');
    const finalUrl = urlPath + props.search;
    history.pushState({}, finalUrl, finalUrl);
    const pushToHistory = new CustomEvent('pushToHistory', { detail: urlPath });
    document.dispatchEvent(pushToHistory);
  }
};

export const cn = (v1: string, v2: string): string => {
  const cssVersion = ConfigSlice.selectors.cssVersion(store.getState());
  switch (cssVersion) {
    case 1:
      return v1;
    case 2:
      return v2;
  }
};

export function containsHtml(str?: string): boolean {
  return /<([a-z][a-z0-9]*)\b[^>]*>(.*?)<\/\1>|<([a-z][a-z0-9]*)\b[^>]*\/?>/i.test(str || '');
}

export function getZip(zip: string = '', zipLength: number = 5): string {
  const digits = zip.match(/\d+/g) ?? [];
  return digits.join('').slice(0, zipLength);
}

type CSSKeys = Exclude<keyof CSSStyleDeclaration, 'length' | 'parentRule'>;
type CSSKeyValue = Partial<Record<CSSKeys, string | number>>;
export function setElementStyles<T extends HTMLElement>(ref: RefObject<T | null>, styles: CSSKeyValue): void {
  const element = ref.current;
  if (!element) return;
  (Object.keys(styles) as Array<keyof CSSKeyValue>).forEach((key) => {
    const value = styles[key];
    if (value !== undefined) {
      (element.style as any)[key] = typeof value === 'string' ? value : value + 'px';
    }
  });
}

type DomRectKey = Exclude<keyof DOMRect, 'toJSON'>;
export function getElementSize<T extends HTMLElement>(
  ref: RefObject<T | null>,
  key: DomRectKey,
  fallbackValue: number = 0,
): number {
  return ref?.current?.getBoundingClientRect()[key] || fallbackValue;
}
